import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBundeslaender } from '../utils/einwohnermeldeamtHelpers';
import { BundeslandInfo } from '../types/einwohnermeldeamt';
import RatingBadge from './RatingBadge';
import { buildProductRatingSchema } from '../config/rating';

const Footer: React.FC = () => {
  const [bundeslaender, setBundeslaender] = useState<BundeslandInfo[]>([]);

  useEffect(() => {
    const loadBundeslaender = async () => {
      try {
        const data = await getBundeslaender();
        setBundeslaender(data);
      } catch (error) {
        console.error('Error loading bundeslaender:', error);
      }
    };
    loadBundeslaender();
  }, []);

  // Inject Product + AggregateRating JSON-LD into <head>
  useEffect(() => {
    const SELECTOR = 'script[data-schema="product-rating"]';
    let scriptEl = document.querySelector(SELECTOR) as HTMLScriptElement | null;

    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.setAttribute('type', 'application/ld+json');
      scriptEl.setAttribute('data-schema', 'product-rating');
      document.head.appendChild(scriptEl);
    }

    scriptEl.textContent = JSON.stringify(buildProductRatingSchema());

    return () => {
      const el = document.querySelector(SELECTOR);
      if (el) el.remove();
    };
  }, []);

  return (
    <footer className="mt-16 pt-8 pb-6 border-t border-gray-200 text-sm text-gray-500">
      <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
        {/* Einwohnermeldeamt Section */}
        {bundeslaender.length > 0 && (
          <div className="mb-6">
            <h3 className="text-center font-semibold text-gray-700 mb-3">Einwohnermeldeämter</h3>
            <div className="text-center mb-3">
              <Link 
                to="/einwohnermeldeamt" 
                className="hover:text-prussian-blue transition-colors font-medium"
              >
                Einwohnermeldeämter Deutschland
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              {bundeslaender.map((bundesland) => (
                <Link
                  key={bundesland.slug}
                  to={`/einwohnermeldeamt/${bundesland.slug}`}
                  className="hover:text-prussian-blue transition-colors"
                >
                  {bundesland.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Legal Links */}
        <div className="flex flex-wrap justify-center gap-6 mb-4 pt-4 border-t border-gray-200">
          <Link to="/kontaktformular" className="hover:text-prussian-blue transition-colors">
            Kontakt
          </Link>
          <Link to="/impressum" target="_blank" rel="noopener noreferrer" className="hover:text-prussian-blue transition-colors">
            Impressum
          </Link>
          <Link to="/agb" target="_blank" rel="noopener noreferrer" className="hover:text-prussian-blue transition-colors">
            AGB
          </Link>
          <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" className="hover:text-prussian-blue transition-colors">
            Datenschutzerklärung
          </Link>
          <Link to="/lieferungen-leistung" target="_blank" rel="noopener noreferrer" className="hover:text-prussian-blue transition-colors">
            Lieferung & Leistung
          </Link>
          <Link to="/widerrufsbelehrung" target="_blank" rel="noopener noreferrer" className="hover:text-prussian-blue transition-colors">
            Widerrufsbelehrung
          </Link>
        </div>

        {/* Vertrag widerrufen */}
        <div className="flex justify-center mb-4">
          <Link to="/vertrag-widerrufen" className="hover:text-prussian-blue transition-colors">
            Vertrag widerrufen
          </Link>
        </div>

        {/* Rating Badge */}
        <div className="flex justify-center py-4 border-t border-gray-200">
          <RatingBadge />
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-center">
          Bitte beachten Sie, dass AdressFinder.org ein unabhängiger Online-Dienstleister ist. Wir sind keine
          Behörde und können für die Richtigkeit und Aktualität der von den Melderegistern zur Verfügung
          gestellten Daten keine Garantie übernehmen.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
